import * as ArtifactUtils from '@wandb/weave/common/util/artifacts';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {notEmpty} from '@wandb/weave/common/util/obj';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {capitalizeFirst} from '@wandb/weave/core';
import React, {useCallback, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {SortFunction} from 'react-table';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Grid, Header} from 'semantic-ui-react';

import {TabWaveLoader} from '../../components/RunDetails/TabWaveLoader';
import {SectionHeaderSubText} from '../../components/Section';
import WBReactTable from '../../components/WBReactTable';
import {useAutoRefreshSettings} from '../../containers/AutoRefreshSettings';
import {
  ArtifactEdge,
  InputArtifactEdge,
  useRunArtifactVersionsQuery,
} from '../../generated/graphql';
import DOC_URLS from '../../util/doc_urls';
import {propagateErrorsContext} from '../../util/errors';
import {trackPageViewed} from '../../util/navigation';
import {useStatsigGateWorkspaceConsolidation} from '../../util/rampFeatureFlags';
import * as Urls from '../../util/urls';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {useBasePollInterval} from './useBasePollInterval';

const PAGE_SIZE = 20;

type ArtifactType = 'inputArtifacts' | 'outputArtifacts';
type ArtifactQueryType = ReturnType<typeof useRunArtifactVersions>['artifacts'];

type ArtifactEdgeTypes = ArtifactEdge | InputArtifactEdge;

const useRunArtifactVersions = (props: {
  entityName: string;
  projectName: string;
  runName: string;
  artifactsType: ArtifactType;
}) => {
  const {autoRefreshEnabled} = useAutoRefreshSettings();
  const {entityName, projectName, runName, artifactsType} = props;

  // First do one time query to get run state
  const queryRunState = useRunArtifactVersionsQuery({
    context: propagateErrorsContext(),
    variables: {
      entityName,
      projectName,
      runName,
      // We don't need input or output artifacts, we will query in the next polling enabled query
      includeInput: false,
      includeOutput: false,
    },
  });

  const run = queryRunState.data?.project?.run;

  // Poll base on run state
  const pollInterval = useBasePollInterval({
    runState: run?.state ?? undefined,
    autoRefreshEnabled,
  });

  // Then do polling enabled query for artifacts base on input/output type
  const queryArtifacts = useRunArtifactVersionsQuery({
    context: propagateErrorsContext(),
    variables: {
      entityName,
      projectName,
      runName,
      includeInput: artifactsType === 'inputArtifacts',
      includeOutput: artifactsType === 'outputArtifacts',
    },
    pollInterval,
  });

  const edges = queryArtifacts.data?.project?.run?.[artifactsType]?.edges ?? [];
  const artifacts =
    (edges as ArtifactEdgeTypes[]).map(e => e.node).filter(notEmpty) ?? [];
  const total =
    queryArtifacts.data?.project?.run?.[artifactsType]?.totalCount ??
    artifacts.length;

  return {...queryArtifacts, artifacts, total};
};

interface RunArtifactsProps {
  entityName: string;
  projectName: string;
  runName: string;
  groupName?: string;
  sweepName?: string;
}

export const RunArtifactGrid: React.FC<RunArtifactsProps> = props => {
  const inputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'inputArtifacts',
  });
  const outputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'outputArtifacts',
  });

  const loading = inputArtifacts.loading || outputArtifacts.loading;
  const hasArtifacts =
    inputArtifacts.total !== 0 || outputArtifacts.total !== 0;

  if (loading && hasArtifacts) {
    return (
      <div style={{position: 'relative', height: 300}}>
        <Loader name="run-artifact-grid" size="huge" />
      </div>
    );
  }
  if (!hasArtifacts) {
    return null;
  }

  const hasBothArtifacts =
    inputArtifacts.total > 0 && outputArtifacts.total > 0;
  return (
    <Grid.Row columns={hasBothArtifacts ? 2 : 1}>
      {inputArtifacts.total > 0 && (
        <Grid.Column>
          <Header>Artifact Inputs</Header>
          <p className="hint-text">
            This run consumed these artifacts as inputs.{' '}
            <TargetBlank href={DOC_URLS.artifacts}>Learn more</TargetBlank>
          </p>
          <ArtifactsTable
            {...inputArtifacts}
            artifactsType="inputArtifacts"
            noHeader
          />
        </Grid.Column>
      )}
      {outputArtifacts.total > 0 && (
        <Grid.Column>
          <Header>Artifact Outputs</Header>
          <p className="hint-text">
            This run produced these artifacts as outputs. Total:{' '}
            {outputArtifacts.total}
            {outputArtifacts.total > 1000 ? ' (showing first 1000)' : ''}
            {'. '}
            <TargetBlank href={DOC_URLS.artifacts}>Learn more</TargetBlank>
          </p>
          <ArtifactsTable
            {...outputArtifacts}
            artifactsType="outputArtifacts"
            noHeader
          />
        </Grid.Column>
      )}
    </Grid.Row>
  );
};

const RunArtifacts = (props: RunArtifactsProps) => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  // for page view tracking
  useEffect(() => {
    let originPageName = 'Run';
    if (props.groupName != null) {
      originPageName = 'Group run';
    } else if (props.sweepName != null) {
      originPageName = 'Sweep run';
    }
    trackPageViewed(originPageName, 'Artifacts');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'inputArtifacts',
  });
  const outputArtifacts = useRunArtifactVersions({
    ...props,
    artifactsType: 'outputArtifacts',
  });

  const loaded = !inputArtifacts.loading && !outputArtifacts.loading;
  const hasOutputArtifacts =
    (outputArtifacts.data?.project?.run?.outputArtifacts?.totalCount ?? -1) > 0;
  const hasInputArtifacts =
    (inputArtifacts.data?.project?.run?.inputArtifacts?.totalCount ?? -1) > 0;
  const hasArtifacts = hasOutputArtifacts || hasInputArtifacts;
  if (!loaded) {
    return <TabWaveLoader />;
  }
  return (
    <div style={{padding: isWorkspaceConsolidationEnabled ? '24px 16px' : 64}}>
      {!hasArtifacts && loaded ? (
        <p>No artifacts are used in this run.</p>
      ) : (
        <>
          <ArtifactsTable {...inputArtifacts} artifactsType="inputArtifacts" />
          <ArtifactsTable
            {...outputArtifacts}
            artifactsType="outputArtifacts"
          />
        </>
      )}
    </div>
  );
};

export default RunArtifacts;

interface ArtifactsTableProps {
  total: number;
  artifacts: ArtifactQueryType;
  artifactsType: ArtifactType;
  loading: boolean;
  noHeader?: boolean;
}

const ArtifactsTable: React.FC<ArtifactsTableProps> = React.memo(props => {
  // We just have 2 types in the table, string and numbers
  const defaultSortMethod: SortFunction = useCallback((a, b) => {
    // check compare type, and set values accordingly
    let aVal = a;
    let bVal = b;
    if (typeof a.props.children === 'number') {
      aVal = a.props.children;
      bVal = b.props.children;
    } else {
      // No fields in this table is null, this is just in case check
      if (a.props.children != null && b.props.children != null) {
        aVal = (a.props.children as string).toLowerCase();
        bVal = (b.props.children as string).toLowerCase();
      }
    }

    // perform sort
    if (aVal > bVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }, []);

  const {loading, total, artifacts, artifactsType, noHeader = false} = props;

  if (loading && total === 0) {
    return (
      <TailwindContents>
        <div className="relative h-[300px]">
          <Loader name="artifacts-table" size="huge" />
        </div>
      </TailwindContents>
    );
  }

  if (total === 0) {
    return null;
  }

  const columns = [
    {Header: 'Type', accessor: 'type', width: 180},
    {Header: 'Name', accessor: 'name'},
    {Header: 'Consumer count', accessor: 'count', width: 240},
  ];

  const rows = artifacts.map(a => {
    const {usedBy} = a;
    const displayAlias = ArtifactUtils.artifactNiceName(a);
    const url = Urls.artifact({
      entityName: a.artifactType.project.entity.name,
      projectName: a.artifactType.project.name,
      artifactTypeName: a.artifactType.name,
      artifactCollectionName: a.artifactSequence.name,
      artifactCommitHash: `v${a.versionIndex}`,
    });

    return {
      searchString: `${displayAlias}${a.artifactType.name}`,
      row: {
        type: <Link to={url}>{a.artifactType.name}</Link>,
        name: <Link to={url}>{displayAlias}</Link>,
        count: <Link to={url}>{usedBy.totalCount}</Link>,
      },
    };
  });

  return (
    <>
      {!noHeader && (
        <Header as="h2">
          {capitalizeFirst(artifactsType.replace('Artifacts', ''))} artifacts
          <br />
          <SectionHeaderSubText>
            Total artifacts: {total}
            {total > 1000 ? '. Table view is limited to 1000 artifacts.' : ''}
          </SectionHeaderSubText>
        </Header>
      )}
      {loading ? (
        <div style={{position: 'relative', height: 200}}>
          <Loader name="artifacts-table" size="huge" />
        </div>
      ) : (
        <WBReactTable
          className="run-artifacts-table"
          pageSize={PAGE_SIZE}
          data={rows}
          columns={columns}
          sortable={true}
          defaultSortMethod={defaultSortMethod}
        />
      )}
    </>
  );
});
